import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import notificationService, {
  requestNotificationPermission as requestFCMPermission,
} from "../services/notificationService";

export const usePWA = () => {
  const { churchUser } = useAuth();

  // Estados do PWA
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [swRegistration, setSwRegistration] = useState(null);

  // Estados de notificação
  const [notificationPermission, setNotificationPermission] = useState(
    notificationService.getPermissionStatus()
  );
  const [fcmToken, setFcmToken] = useState(null);

  // Detectar se está instalado
  useEffect(() => {
    const checkIfInstalled = () => {
      // Verifica se está rodando como PWA
      const isInStandaloneMode = window.matchMedia(
        "(display-mode: standalone)"
      ).matches;
      const isInWebAppMode = window.navigator.standalone === true;
      const isInPWAMode = isInStandaloneMode || isInWebAppMode;

      setIsInstalled(isInPWAMode);
    };

    checkIfInstalled();

    // Escuta mudanças no display mode
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    mediaQuery.addEventListener("change", checkIfInstalled);

    return () => mediaQuery.removeEventListener("change", checkIfInstalled);
  }, []);

  // Registrar Service Worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          setSwRegistration(registration);

          // Verificar atualizações
          registration.addEventListener("updatefound", () => {
            console.log("[PWA] Nova versão disponível");
          });
        })
        .catch((error) => {
          console.error("[PWA] Erro ao registrar Service Worker:", error);
        });
    }
  }, []);

  // Detectar evento de instalação
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log("[PWA] beforeinstallprompt event fired");
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      console.log("[PWA] App foi instalado");
      setIsInstalled(true);
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  // Monitorar status online/offline
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Registrar token FCM automaticamente quando usuário logar
  useEffect(() => {
    const registerFCMToken = async () => {
      const userId = churchUser?.uid || churchUser?.email;

      if (userId && notificationPermission === "granted") {
        try {
          const token = await notificationService.requestPermissionAndGetToken(
            userId
          );
          if (token) {
            setFcmToken(token);
          }
        } catch (error) {
          console.error("[PWA] Erro ao registrar token FCM:", error);
        }
      }
    };

    registerFCMToken();
  }, [
    churchUser?.membroId,
    churchUser?.uid,
    churchUser?.email,
    notificationPermission,
  ]);

  // Função para instalar o PWA
  const installPWA = useCallback(async () => {
    if (!installPrompt) {
      console.warn("[PWA] Prompt de instalação não disponível");
      return false;
    }

    try {
      const result = await installPrompt.prompt();
      console.log("[PWA] Resultado da instalação:", result);

      if (result.outcome === "accepted") {
        setIsInstallable(false);
        setInstallPrompt(null);
        return true;
      }

      return false;
    } catch (error) {
      console.error("[PWA] Erro durante instalação:", error);
      return false;
    }
  }, [installPrompt]);

  // Função para solicitar permissão de notificação
  const requestNotificationPermission = useCallback(async (userId) => {
    if (!notificationService.isNotificationSupported()) {
      console.warn("[PWA] Notificações não suportadas");
      return "not-supported";
    }

    try {
      const token = await requestFCMPermission(userId);

      if (token) {
        setFcmToken(token);
        setNotificationPermission("granted");
        console.log(
          "[PWA] Permissão para notificações concedida e token registrado"
        );
        return "granted";
      } else {
        setNotificationPermission("denied");
        return "denied";
      }
    } catch (error) {
      console.error("[PWA] Erro ao solicitar permissão:", error);
      setNotificationPermission("denied");
      return "denied";
    }
  }, []);

  // Função para enviar notificação local via Service Worker (compatível com PWA)
  const sendLocalNotification = useCallback(
    async (title, options = {}) => {
      if (notificationPermission === "granted") {
        try {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(title, {
            icon: "/icons/icon-192x192.png",
            badge: "/icons/icon-72x72.png",
            tag: "local-notification",
            ...options,
          });
          return true;
        } catch (error) {
          console.error("[PWA] Erro ao enviar notificação local:", error);
          return null;
        }
      }

      console.warn("[PWA] Permissão de notificação não concedida");
      return null;
    },
    [notificationPermission]
  );

  // Função para enviar notificação de teste
  const sendTestNotification = useCallback(
    async (userId) => {
      try {
        // Garantir que o token está registrado primeiro
        if (!fcmToken && notificationPermission === "granted") {
          console.log(
            "[PWA] Registrando token FCM antes de enviar notificação..."
          );
          const token = await notificationService.requestPermissionAndGetToken(
            userId
          );
          if (token) {
            setFcmToken(token);
          } else {
            console.error("[PWA] Falha ao registrar token FCM");
            return false;
          }
        }

        const success = await notificationService.sendTestNotification(userId);

        if (success) {
          return true;
        } else {
          console.warn("[PWA] Falha ao enviar notificação de teste");
          return false;
        }
      } catch (error) {
        console.error("[PWA] Erro ao enviar notificação de teste:", error);
        return false;
      }
    },
    [fcmToken, notificationPermission]
  );

  // Função para subscrever a tópico
  const subscribeToTopic = useCallback(async (userId, topic) => {
    try {
      const success = await notificationService.subscribeToTopic(userId, topic);

      if (success) {
        console.log(`[PWA] Subscrito ao tópico: ${topic}`);
        return true;
      } else {
        console.warn(`[PWA] Falha ao subscrever ao tópico: ${topic}`);
        return false;
      }
    } catch (error) {
      console.error("[PWA] Erro ao subscrever ao tópico:", error);
      return false;
    }
  }, []);

  // Função para atualizar o app
  const updateApp = useCallback(() => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
      window.location.reload();
    }
  }, [swRegistration]);

  // Função para detectar se está offline
  const isAppOffline = useCallback(() => {
    return !isOnline;
  }, [isOnline]);

  return {
    // Estados
    isInstallable,
    isInstalled,
    isOnline,
    notificationPermission,
    swRegistration,
    fcmToken,

    // Funções
    installPWA,
    requestNotificationPermission,
    sendLocalNotification,
    sendTestNotification,
    subscribeToTopic,
    updateApp,
    isAppOffline,

    // Status checks
    canInstall: isInstallable && !isInstalled,
    needsNotificationPermission: notificationPermission === "default",
    hasNotificationPermission: notificationPermission === "granted",
    isNotificationSupported: notificationService.isNotificationSupported(),
  };
};

// Função auxiliar para converter chave VAPID
function convertVapidKey(vapidKey) {
  const padding = "=".repeat((4 - (vapidKey.length % 4)) % 4);
  const base64 = (vapidKey + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export default usePWA;
